/**
 * Drop in url param handlers.
 * Catches url params and updates state via hooks.
 * Generally used at the root of the App outside any step condition.
 */
import { useEffect } from 'preact/compat';
import { Candidate, WPConfig } from '../types';
import { useCart, normalizeCurrency, CartActionType } from './cart';
import { useUser } from './user';

/**
 * Handle setting amount from url param.
 * Use only static candidate lists, no api.
 * @param {Candidate[]} candidates The candidates to recieve amount updates.
 */
export const useAmountUrlParam = (candidates: Candidate[]) => {
	const { dispatch } = useCart();

	/**
	 * Runs on init mount.
	 * - Checks for url params. Currently checks for '?amount={value}'.
	 * - If params are present, update cart.
	 */
	useEffect(() => {
		const query = new URLSearchParams(window.location.search);
		let am: string|number = query.get('amount');
		am = normalizeCurrency(am).value as number;
		if (am !== 0) {
			candidates.forEach((c) => {
				dispatch({
					type: CartActionType.MANUAL_INPUT,
					payload: {
						...c,
						amount: am as number,
					},
				});
			});
		}
	}, []);
};

/**
 * Handle extracting a possible query param.
 *
 * @param queryStr window.location.search.
 * @param paramKeys URL param key array.
 * @param outputType string|number.
 */
export function parseQueryParam(queryStr: string, paramKeys: string[]): string|false {
	const query = new URLSearchParams(queryStr);
	let s: string = '';
	for (let i = 0; i < paramKeys.length; i += 1) {
		if (query.has(paramKeys[i])) {
			s = query.get(paramKeys[i]) as string;
			break;
		}
	}
	if (s === '') {
		return false;
	}
	return s;
}

/**
 * Source Code Url Param Hook.
 *
 * There are two source code types:
 * - API param: sourceCodeName, url param: source, type: string
 * - API param: sourceCodeId, url param: sourceid, type: number
 *
 * sourceCodeName possibly exists in theme config as source_code.
 * sourceCodeId takes precedence over sourceCodeName,
 * EXCEPT caging forms — FE provided sourceCodeName takes precedence.
 */
export const useSourceCodeUrlParam = (theme: WPConfig, refresh = false) => {
	const { dispatch: dispatchUser } = useUser();

	const {
		source_code: defaultSourceCode,
	} = theme;

	// Add more if needed.
	const sourceCodeNameParams = [
		'source',
	];

	// Add more if needed.
	const sourceCodeIdParams = [
		'sourceid',
	];

	useEffect(() => {
		let outPut = {};
		let sourceCodeName = parseQueryParam(window.location.search, sourceCodeNameParams);
		if (!sourceCodeName) {
			sourceCodeName = defaultSourceCode;
		}
		outPut = {
			sourceCodeName,
		};

		// Only add if sourceCodeName exists.
		let sourceCodeId: any = parseQueryParam(window.location.search, sourceCodeIdParams);
		if (sourceCodeId) {
			sourceCodeId = parseInt(sourceCodeId, 10);
			if (!Number.isNaN(sourceCodeId) && typeof sourceCodeId === 'number') {
				outPut = {
					...outPut,
					sourceCodeId,
				};
			}
		}

		dispatchUser({
			type: 'UPDATE_USER',
			payload: {
				...outPut,
			},
		});
	}, [refresh]);
};

/**
 * Composite hook for url params.
 */
const useUrlParams = (candidates: Candidate[], theme: WPConfig, refresh = false) => {
	useAmountUrlParam(candidates);
	useSourceCodeUrlParam(theme, refresh);
};

export default useUrlParams;
